import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Brand } from '@/components/dashboard/brand'
import { AddCustomerDialog } from '@/components/dashboard/add-customer-dialog'
import { NAV_ITEMS } from '@/constants/dashboard'

interface SidebarProps {
    onNavigate?: () => void
    onClose?: () => void
}

const isActive = (pathname: string, href: string): boolean => {
    return href === '/' ? pathname === '/' : pathname === href || pathname.startsWith(`${href}/`)
}

export const Sidebar: FC<SidebarProps> = ({ onNavigate, onClose }): ReactNode => {
    const { pathname } = useRouter()

    return (
        <div className='flex h-full flex-col bg-sidebar text-sidebar-foreground'>
            <div className='flex h-16 shrink-0 items-center justify-between gap-2 px-5'>
                <Link
                    href='/'
                    onClick={onNavigate}
                    className='rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring'
                >
                    <Brand />
                </Link>

                {onClose && (
                    <button
                        type='button'
                        onClick={onClose}
                        className='rounded-lg p-2 text-sidebar-foreground transition-colors hover:bg-sidebar-accent hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring'
                    >
                        <X aria-hidden='true' className='size-5' />
                        <span className='sr-only'>Close menu</span>
                    </button>
                )}
            </div>

            <nav aria-label='Main' className='flex-1 overflow-y-auto px-3 py-4'>
                <ul className='space-y-1'>
                    {NAV_ITEMS.map(item => {
                        const active = isActive(pathname, item.href)
                        const Icon = item.icon

                        return (
                            <li key={item.href}>
                                <Link
                                    href={item.href}
                                    onClick={onNavigate}
                                    aria-current={active ? 'page' : undefined}
                                    className={cn(
                                        'relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring',
                                        active
                                            ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                                            : 'hover:bg-sidebar-accent/60 hover:text-white'
                                    )}
                                >
                                    {active && (
                                        <span
                                            aria-hidden='true'
                                            className='absolute inset-y-2 left-0 w-1 rounded-r-full bg-sidebar-primary'
                                        />
                                    )}

                                    <Icon
                                        aria-hidden='true'
                                        className={cn('size-5', active ? 'text-sidebar-primary' : 'text-sidebar-foreground')}
                                    />

                                    {item.label}
                                </Link>
                            </li>
                        )
                    })}
                </ul>
            </nav>

            <div className='shrink-0 border-t border-sidebar-border p-4'>
                <AddCustomerDialog variant='sidebar' />

                <p className='mt-3 text-center text-xs text-[#7f889c]'>
                    Dates and times are shown in UTC
                </p>
            </div>
        </div>
    )
}
